import { SPACE_ACCESS, spaceAccessOf } from '../features/pricing/space-access'
import { localStorage } from './local-storage'

// Guards und Entscheidung für den automatischen Standortwechsel bei
// abgelaufenem Zugang.
//
// Der Wechsel ist ein Full Reload (restartDashboard). Ohne Gedächtnis könnte
// er sich im Kreis drehen oder eine bewusste Entscheidung des Nutzers
// überschreiben. Deshalb liegt der Stand in localStorage, nicht im Speicher.
// Er überlebt den Reload und ist je Nutzer getrennt. Bei signOut räumt
// localStorage.clear() mit auf.
//
// Drei Dinge werden gemerkt.
// deliberate: Standorte, die der Nutzer selbst gewählt hat. Sie werden
// 24 Stunden lang nicht automatisch verlassen. Die Frist verhindert, dass ein
// einmaliger Klick vor Wochen den Wechsel für immer abschaltet.
// last: der letzte automatische Wechsel. 24 Stunden lang gibt es keinen
// zweiten, das bricht jede Schleife, auch über Tabs und Remounts.
// notice: Hinweis für das Modal im Zielstandort, 10 Minuten gültig.
const KEY_PREFIX = 'space-auto-switch:'

export const DELIBERATE_TTL_MS = 24 * 60 * 60 * 1000
export const COOLDOWN_MS = 24 * 60 * 60 * 1000
export const NOTICE_TTL_MS = 10 * 60 * 1000

const keyOf = (user) => `${KEY_PREFIX}${user}`

const emptyState = () => ({ deliberate: {}, last: null, notice: null })

const isObject = (value) => !!value && typeof value === 'object'

const read = (user) => {
  if (!user) return emptyState()

  try {
    const parsed = JSON.parse(localStorage.getItem(keyOf(user)) || 'null')
    if (!isObject(parsed)) return emptyState()

    return {
      deliberate: isObject(parsed.deliberate) ? parsed.deliberate : {},
      last: isObject(parsed.last) ? parsed.last : null,
      notice: isObject(parsed.notice) ? parsed.notice : null
    }
  } catch {
    // krummer Eintrag darf den Login nicht killen
    return emptyState()
  }
}

// Der localStorage-Helper schluckt Fehler (Quota, Privatmodus). Ob der Wert
// angekommen ist, zeigt nur das Zurücklesen. Ohne schreibbaren Speicher gibt
// es keinen Cooldown, also auch keinen automatischen Wechsel.
const write = (user, state) => {
  if (!user) return false

  const serialized = JSON.stringify(state)
  localStorage.setItem(keyOf(user), serialized)

  return localStorage.getItem(keyOf(user)) === serialized
}

// Auch Zeitstempel weit in der Zukunft gelten als abgelaufen, sonst sperrt ein
// kaputter Wert oder eine verstellte Uhr für immer.
const isRecent = (at, ttl, now) =>
  typeof at === 'number' && Math.abs(now - at) < ttl

const pruneDeliberate = (deliberate, now) =>
  Object.fromEntries(
    Object.entries(deliberate).filter(([, at]) =>
      isRecent(at, DELIBERATE_TTL_MS, now)
    )
  )

// Vor jedem Wechsel, den der Nutzer selbst auslöst (Switcher, Auswahllisten)
export const markDeliberateSpaceSwitch = ({
  user,
  spaceId,
  now = Date.now()
}) => {
  if (!user || !spaceId) return false

  const state = read(user)

  return write(user, {
    ...state,
    deliberate: { ...pruneDeliberate(state.deliberate, now), [spaceId]: now }
  })
}

// Darf der aktuelle Standort automatisch verlassen werden? Nein, wenn der
// Nutzer ihn selbst gewählt hat oder gerade erst automatisch gewechselt wurde.
export const canAutoSwitch = ({ user, spaceId, now = Date.now() }) => {
  if (!user || !spaceId) return false

  const { deliberate, last } = read(user)

  if (isRecent(deliberate[spaceId], DELIBERATE_TTL_MS, now)) return false
  if (last && isRecent(last.at, COOLDOWN_MS, now)) return false

  return true
}

// Vor dem updateUser aufrufen. Liefert false, wenn der Stand nicht
// gespeichert werden konnte, dann darf nicht automatisch gewechselt werden.
export const recordAutoSwitch = ({
  user,
  from,
  to,
  fromName,
  now = Date.now()
}) => {
  if (!user || !from || !to) return false

  return write(user, {
    ...read(user),
    last: { from, to, at: now },
    notice: { to, from_name: fromName || '', at: now }
  })
}

// Schlägt das updateUser fehl, gab es keinen Reload und damit keine Schleife.
// Ein Netzaussetzer soll den Nutzer nicht 24 Stunden sperren.
export const rollbackAutoSwitch = ({ user }) =>
  write(user, { ...read(user), last: null, notice: null })

// Hinweis für das Modal. Er passt nur auf den Zielstandort und nur kurz.
export const readAutoSwitchNotice = ({
  user,
  activeSpaceId,
  now = Date.now()
}) => {
  const { notice } = read(user)

  if (
    !notice ||
    notice.to !== activeSpaceId ||
    !isRecent(notice.at, NOTICE_TTL_MS, now)
  ) {
    return null
  }

  return {
    fromName: typeof notice.from_name === 'string' ? notice.from_name : ''
  }
}

export const clearAutoSwitchNotice = ({ user }) => {
  const state = read(user)

  if (state.notice) write(user, { ...state, notice: null })
}

export const SPACE_FALLBACK = Object.freeze({
  NONE: 'NONE',
  CHOOSE: 'CHOOSE',
  AUTO_SWITCH: 'AUTO_SWITCH'
})

const ACCESS_RANK = { [SPACE_ACCESS.ACTIVE]: 0, [SPACE_ACCESS.TRIAL]: 1 }

const lastSeenOf = ({ space }) => space.last_seen || ''

// Bezahlte vor Testphasen, dann zuletzt benutzte zuerst
const byAccessThenLastSeen = (a, b) => {
  const rank = ACCESS_RANK[a.access] - ACCESS_RANK[b.access]
  if (rank !== 0) return rank

  const [x, y] = [lastSeenOf(a), lastSeenOf(b)]
  if (x === y) return 0

  return x < y ? 1 : -1
}

const sameIds = (a, b) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|')

/**
 * Was soll die Abgelaufen-Ansicht tun? Rein, damit alle Regeln ohne Rendern
 * testbar sind.
 *
 * NONE: bisherige Ansicht. CHOOSE: Auswahl der nutzbaren Standorte.
 * AUTO_SWITCH: genau ein nutzbarer Standort, es wird gewechselt.
 *
 * Voraussetzungen für alles außer NONE
 * - Das Backend bestätigt den aktiven Standort als abgelaufen. Der Context
 *   kann auf Daten aus dem persistierten Cache stehen, darauf wird nicht
 *   gehandelt.
 * - Der aktive Space gehört zum angezeigten Partner. Das schließt die
 *   Admin-Impersonation aus (localStorage admin überschreibt nur partner_id).
 *
 * Kandidaten sind nur Spaces, die der Nutzer laut employees besitzt und zu
 * denen das Backend Payments geliefert hat. Ein employees-Space ohne Antwort
 * ist unbekannt und nie nutzbar, ein Antwort-Space ohne employees-Eintrag wird
 * nie gezeigt.
 *
 * Automatisch wird nur gewechselt, wenn beide Seiten dieselben anderen Spaces
 * kennen und die Antwort nicht gekappt wurde, sonst könnte es einen weiteren
 * nutzbaren Standort geben und die Wahl wäre willkürlich.
 *
 * @param {object} params
 * @param {{ id: string, partner_id: string }} params.activeSpace
 * @param {string} params.partnerId Partner des angezeigten Dashboards
 * @param {object[]} params.employees alle EMPLOYEE-Spaces des Nutzers
 * @param {{ spaces: object[], truncated: boolean }} params.spacesAccess
 * @param {boolean} params.autoAllowed Guards (canAutoSwitch, Tab sichtbar)
 * @returns {{ kind: string, candidates: { space: object, access: string }[] }}
 */
export const planSpaceFallback = ({
  activeSpace,
  partnerId,
  employees,
  spacesAccess,
  autoAllowed,
  now = Date.now()
}) => {
  const none = { kind: SPACE_FALLBACK.NONE, candidates: [] }

  if (!activeSpace?.id || activeSpace.partner_id !== partnerId) return none

  const answered = new Map(
    spacesAccess.spaces.map((space) => [space.space_id, space])
  )
  const active = answered.get(activeSpace.id)

  if (!active || spaceAccessOf(active.payments, now) !== SPACE_ACCESS.EXPIRED) {
    return none
  }

  const others = employees.filter(({ id }) => id !== activeSpace.id)

  const candidates = others
    .filter(({ id }) => answered.has(id))
    .map((space) => ({
      space,
      access: spaceAccessOf(answered.get(space.id).payments, now)
    }))
    .filter(({ access }) => access !== SPACE_ACCESS.EXPIRED)
    .sort(byAccessThenLastSeen)

  if (!candidates.length) return none

  const isComplete =
    !spacesAccess.truncated &&
    sameIds(
      others.map(({ id }) => id),
      spacesAccess.spaces
        .map(({ space_id }) => space_id)
        .filter((id) => id !== activeSpace.id)
    )

  return {
    kind:
      candidates.length === 1 && isComplete && autoAllowed
        ? SPACE_FALLBACK.AUTO_SWITCH
        : SPACE_FALLBACK.CHOOSE,
    candidates
  }
}
